import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { ARENA } from "@/content/galaxy";
import { AiTag } from "@/components/CaptainCards";
import interiorImg from "@/assets/arena-interior.webp";

export const ArenaShowcase = () => {
  const sectionRef = useRef(null);
  const reduceMotion = useReducedMotion();
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"],
  });
  const imgY = useTransform(
    scrollYProgress,
    [0, 1],
    reduceMotion ? ["0%", "0%"] : ["-7%", "7%"]
  );

  return (
    <section
      id="arena"
      ref={sectionRef}
      data-testid="arena-section"
      className="relative z-10 py-24 md:py-32"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8">
        <Reveal className="max-w-2xl">
          <span className="font-mono-accent text-xs uppercase tracking-[0.3em] text-gl-blue2">
            {ARENA.badge}
          </span>
          <h2 className="mt-4 font-display text-4xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-5xl">
            {ARENA.headline}{" "}
            <span className="text-gradient-brand">{ARENA.city}</span>
          </h2>
          <span
            data-testid="arena-reveal-hint"
            className="mt-4 inline-flex items-center gap-2 rounded-full border border-gl-blue/30 bg-gl-blue/10 px-3.5 py-1.5 font-mono-accent text-[11px] uppercase tracking-[0.22em] text-gl-blue2"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-gl-blue shadow-[0_0_12px_rgba(58,123,255,0.9)] animate-gl-pulse" />
            neuro.io Arena · Wien
          </span>
          <p className="mt-5 max-w-xl text-sm leading-relaxed text-white/65 sm:text-base">
            {ARENA.intro}
          </p>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="relative mt-12 lg:mt-16">
            {/* Main — interior arena */}
            <figure
              data-testid="arena-interior"
              className="group relative overflow-hidden rounded-3xl border border-white/10 bg-[#0b1024] shadow-[0_0_0_1px_rgba(58,123,255,0.10),0_30px_80px_rgba(0,0,0,0.5)]"
            >
              <div className="relative aspect-[16/11] w-full overflow-hidden sm:aspect-[16/9]">
                <motion.img
                  src={interiorImg}
                  alt="Innenansicht einer Arena in Wien mit blau beleuchtetem Galaxy-League-Spielfeld"
                  loading="lazy"
                  style={{ y: imgY, scale: 1.2 }}
                  className="absolute inset-0 h-full w-full object-cover will-change-transform"
                />
                <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-[#070A16] via-[#070A16]/25 to-transparent" />
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_circle_at_72%_8%,rgba(58,123,255,0.18),transparent_60%)]" />
                <AiTag className="absolute right-3 top-3 sm:right-5 sm:top-4" />
              </div>

              <figcaption className="absolute inset-x-0 bottom-0 flex items-end justify-start p-3 sm:justify-between sm:p-8">
                <div className="flex flex-nowrap gap-1.5 sm:flex-wrap sm:gap-2.5 lg:pr-[19rem]">
                  {ARENA.facts.map((f) => (
                    <div
                      key={f.label}
                      className="rounded-lg border border-white/10 bg-[#070A16]/70 px-2 py-1.5 sm:rounded-xl sm:px-3.5 sm:py-2"
                    >
                      <div className="font-display text-[11px] font-bold leading-tight text-white sm:text-sm">
                        {f.value}
                      </div>
                      <div className="font-mono-accent text-[7px] uppercase tracking-[0.15em] text-white/55 sm:text-[9px] sm:tracking-[0.2em]">
                        {f.label}
                      </div>
                    </div>
                  ))}
                </div>
              </figcaption>
            </figure>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
